"use client";

import React from "react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { Menu } from "lucide-react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { BrandModel } from "@/types/brand";
import { CategoryModel } from "@/types/category";
import { cn } from "@/lib/utils";

const MobileMenu = ({
  brands,
  categories,
}: {
  brands: BrandModel[];
  categories: CategoryModel[];
}) => {
  const t = useTranslations();
  const locale = useLocale();

  return (
    <div className="md:hidden">
      <Sheet>
        <SheetTrigger asChild>
          <button className="text-white flex">
            <Menu />
          </button>
        </SheetTrigger>

        <SheetContent
          side={locale === "ar" ? "right" : "left"}
          className="w-[280px] sm:w-[320px] p-5 max-h-screen overflow-y-auto"
        >
          <nav className="flex flex-col gap-4 mt-6">
            {/* Home */}
            <Link href={`/${locale}`} className="font-medium text-sm">
              {t("home")}
            </Link>
            {/* Home */}
            <Link
              href={`/${locale}/about-company`}
              className="font-medium text-sm"
            >
              {t("aboutCompany")}
            </Link>

            {/* Categories */}
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="categories">
                <AccordionTrigger>{t("shopByCategories")}</AccordionTrigger>

                <AccordionContent>
                  <div
                    className={cn(
                      "flex flex-col ",
                      locale === "ar" ? "pr-2  border-r" : "pl-2 border-l",
                    )}
                  >
                    {categories.map((category) => (
                      <div key={category.id}>
                        {category.subcategories?.length ? (
                          <Accordion type="single" collapsible className="px-2">
                            <AccordionItem value={category.id.toString()}>
                              <AccordionTrigger className="text-xs">
                                {category.name}
                              </AccordionTrigger>
                              <AccordionContent
                                className={cn(
                                  locale === "ar" ? "pr-2" : "pl-2",
                                )}
                              >
                                {category.subcategories.map((sub) => (
                                  <Link
                                    key={sub.id}
                                    href={`/${locale}/categories/${category.id}?subcategory=${sub.id}`}
                                    className="block text-xs text-muted-foreground py-1"
                                  >
                                    <SheetClose className="cursor-pointer">
                                      {sub.name}
                                    </SheetClose>
                                  </Link>
                                ))}
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        ) : (
                          <Link
                            href={`/${locale}/categories/${category.id}`}
                            className={cn(
                              "block text-xs py-1",
                              locale === "ar" ? "pr-2" : "pl-2",
                            )}
                          >
                            {category.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Brands */}
              <AccordionItem value="brands">
                <AccordionTrigger>{t("shopByBrands")}</AccordionTrigger>
                <AccordionContent
                  className={cn(locale === "ar" ? "pr-2" : "pl-2")}
                >
                  <div
                    className={cn(
                      "grid grid-cols-2 gap-2",
                      // locale === "ar" ? "pr-2  border-r" : "pl-2 border-l",
                    )}
                  >
                    {brands.map((brand) => (
                      <Link
                        key={brand.id}
                        href={`/${locale}/brands/${brand.id}`}
                        className="text-xs text-muted-foreground"
                      >
                        <SheetClose className="cursor-pointer">
                          {brand.name}
                        </SheetClose>
                      </Link>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileMenu;
